import React, { Component } from 'react';
import { chevronForwardOutline as forward, trashOutline as trash } from 'ionicons/icons';
import {
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonSegment,
  IonSegmentButton,
  IonCheckbox,
  IonModal,
  IonAlert,
  IonToast,
  IonSpinner,
  IonBadge,
  withIonLifeCycle,
} from '@ionic/react';

import VaccinesService from '../services/vaccines';
import { errorMessage } from '../utils/errorMessage';
import '../theme/service.css';

const CF_REGEX = /^[a-zA-Z]{6}[0-9]{2}[a-zA-Z][0-9]{2}[a-zA-Z][0-9]{3}[a-zA-Z]$/;

const EXCERPT_MAX_LENGTH = 120;

// Le date arrivano dall'API come 'Y-m-d H:i': in modale le mostro in formato italiano
function formatDateTime(value) {
  if (!value) return '';
  const [date, time] = value.split(' ');
  const [year, month, day] = (date || '').split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}${time ? ` alle ${time}` : ''}`;
}

// L'HTML di summernote non e' renderizzabile dentro IonLabel: nella lista mostro solo un estratto testuale
function descriptionExcerpt(type) {
  const source = type.app_description || type.description || '';
  const text = source
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= EXCERPT_MAX_LENGTH) return text;

  return `${text.slice(0, EXCERPT_MAX_LENGTH).trimEnd()}...`;
}

class VaccinesPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      types: [],
      rules: [],
      proposals: [],
      loading: true,
      submitting: false,
      toast: null,
      alert: null,
      // proposta in attesa di risposta aperta dalla lista
      proposalGroup: null,
      responding: false,
      // stato modale iscrizione
      modalType: null,
      extraTypeIds: [],
      lockMinor: false,
      forWho: 'me',
      minorName: '',
      minorFiscalCode: '',
      declared: null,
      patientAge: '',
      otherPathologies: '',
      otherVaccinesDone: null,
      otherVaccinesNotes: '',
    };

    this.submit = this.submit.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleTypeClick = this.handleTypeClick.bind(this);
    this.closeProposalModal = this.closeProposalModal.bind(this);
  }

  async ionViewWillEnter() {
    this.setState({ loading: true });
    try {
      const [types, proposals] = await Promise.all([VaccinesService.types(), VaccinesService.proposals()]);

      this.setState({
        types: types.data.data || [],
        rules: types.data.rules || [],
        proposals: proposals.data.data || [],
        loading: false,
      });
    } catch (error) {
      console.error(error);
      this.setState({ loading: false });
    }
  }

  entryForMe(type) {
    return (type.enrolled || []).find((e) => !e.fiscalcode_id) || null;
  }

  // Un appuntamento combinato ha una proposta per vaccino: le raggruppo per appuntamento
  proposalGroupFor(entry) {
    if (!entry) return null;

    return (
      this.state.proposals.find((group) =>
        (group.proposals || []).some((proposal) => proposal.entry_id === entry.entry_id)
      ) || null
    );
  }

  // Vaccini che possono essere somministrati nella stessa seduta di quello scelto:
  // richiedendoli insieme si entra in un'unica lista combinata
  combinableWith(type) {
    if (!type) return [];

    const { types, rules } = this.state;

    return types.filter((other) => {
      if (other.id === type.id) return false;
      if (this.entryForMe(other)) return false;

      const rule = rules.find(
        (r) =>
          (r.vaccine_a_id === type.id && r.vaccine_b_id === other.id) ||
          (r.vaccine_b_id === type.id && r.vaccine_a_id === other.id)
      );

      return !!rule && !!rule.can_be_combined;
    });
  }

  toggleExtraType(id) {
    const { extraTypeIds } = this.state;

    this.setState({
      extraTypeIds: extraTypeIds.includes(id)
        ? extraTypeIds.filter((current) => current !== id)
        : [...extraTypeIds, id],
    });
  }

  handleTypeClick(type) {
    const entry = this.entryForMe(type);

    if (!entry) {
      this.openModal(type);
      return;
    }

    if (entry.status === 'confirmed') {
      this.setState({
        toast: `Appuntamento confermato per ${type.name}: gestiscilo da "I miei vaccini".`,
      });
      return;
    }

    if (entry.status !== 'waiting') {
      const proposalGroup = this.proposalGroupFor(entry);

      if (!proposalGroup) {
        this.setState({
          toast: `Hai già una proposta in corso per ${type.name}: rispondi da "I miei vaccini".`,
        });
        return;
      }

      this.setState({ proposalGroup });
      return;
    }

    this.setState({
      alert: {
        header: 'Sei già in lista',
        message: `Sei già iscritto alla lista d'attesa per ${type.name}. Vuoi cancellare l'iscrizione?`,
        entryId: entry.entry_id,
        type,
      },
    });
  }

  async removeEntry(id) {
    this.setState({ submitting: true });
    try {
      await VaccinesService.removeEntry(id);
      this.setState({ submitting: false, toast: "Iscrizione cancellata: non sei più in lista d'attesa." });
      this.ionViewWillEnter();
    } catch (error) {
      const message = errorMessage(error, 'Errore durante la cancellazione');
      this.setState({ submitting: false, toast: message });
    }
  }

  openModal(type, forWho = 'me') {
    this.setState({
      modalType: type,
      extraTypeIds: [],
      lockMinor: forWho === 'minor',
      forWho,
      minorName: '',
      minorFiscalCode: '',
      declared: null,
      patientAge: '',
      otherPathologies: '',
      otherVaccinesDone: null,
      otherVaccinesNotes: '',
    });
  }

  closeModal() {
    this.setState({ modalType: null });
  }

  closeProposalModal() {
    this.setState({ proposalGroup: null });
  }

  // La risposta vale per tutte le proposte dello stesso appuntamento (vaccini combinati)
  async respondToProposal(response) {
    const { proposalGroup } = this.state;
    const proposalId = (proposalGroup?.proposals || [])[0]?.id;
    if (!proposalId) return;

    this.setState({ responding: true });
    try {
      await VaccinesService.respond(proposalId, response);
      this.setState({
        responding: false,
        proposalGroup: null,
        toast:
          response === 'confirmed'
            ? 'Appuntamento confermato. Ti aspettiamo in farmacia!'
            : "Proposta rifiutata: sei stato reinserito in lista d'attesa.",
      });
      this.ionViewWillEnter();
    } catch (error) {
      const message = errorMessage(error, 'Errore durante la risposta');
      this.setState({ responding: false, proposalGroup: null, toast: message });
      this.ionViewWillEnter();
    }
  }

  // Vaccini scelti che ammettono in lista solo chi li ha già ricevuti in passato
  requiresPriorVaccination() {
    const { modalType, extraTypeIds, types } = this.state;
    if (!modalType) return false;

    const selectedTypes = [modalType, ...types.filter((type) => extraTypeIds.includes(type.id))];

    return selectedTypes.some((type) => type.requires_prior_vaccination);
  }

  canSubmit() {
    const { declared, patientAge, forWho, minorName, minorFiscalCode, otherVaccinesDone } = this.state;
    const age = parseInt(patientAge, 10);
    if (declared === null || !(age >= 1 && age <= 120)) return false;
    if (declared === false && this.requiresPriorVaccination()) return false;
    if (otherVaccinesDone === null) return false;
    if (forWho === 'minor') {
      return minorName.trim().length > 2 && CF_REGEX.test(minorFiscalCode.trim());
    }
    return true;
  }

  async submit() {
    const {
      modalType,
      forWho,
      minorName,
      minorFiscalCode,
      extraTypeIds,
      declared,
      patientAge,
      otherPathologies,
      otherVaccinesDone,
      otherVaccinesNotes,
    } = this.state;
    if (!modalType || !this.canSubmit()) return;

    this.setState({ submitting: true });
    try {
      await VaccinesService.enroll({
        vaccine_type_ids: [modalType.id, ...extraTypeIds],
        for_minor: forWho === 'minor',
        minor_name: forWho === 'minor' ? minorName.trim() : null,
        minor_fiscal_code: forWho === 'minor' ? minorFiscalCode.trim().toUpperCase() : null,
        prior_vaccination_declared: declared,
        patient_age: parseInt(patientAge, 10),
        other_pathologies: otherPathologies || null,
        other_vaccines_done: otherVaccinesDone,
        other_vaccines_notes: otherVaccinesDone ? otherVaccinesNotes || null : null,
      });

      this.setState({
        submitting: false,
        modalType: null,
        toast: 'Iscrizione registrata. Ti avviseremo appena ci saranno dosi.',
      });

      this.ionViewWillEnter();
    } catch (error) {
      this.setState({ submitting: false, toast: errorMessage(error, "Errore durante l'iscrizione") });
    }
  }

  renderModal() {
    const {
      modalType,
      extraTypeIds,
      lockMinor,
      forWho,
      minorName,
      minorFiscalCode,
      declared,
      patientAge,
      otherPathologies,
      otherVaccinesDone,
      otherVaccinesNotes,
      submitting,
    } = this.state;

    const combinable = this.combinableWith(modalType);
    const requiresPrior = this.requiresPriorVaccination();

    return (
      <IonModal isOpen={!!modalType} onDidDismiss={this.closeModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{modalType?.name}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={this.closeModal}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          {modalType?.app_description ? (
            <div dangerouslySetInnerHTML={{ __html: modalType.app_description }} />
          ) : (
            modalType?.description && <p>{modalType.description}</p>
          )}

          {lockMinor ? (
            <p style={{ color: 'var(--ion-color-medium)' }}>
              Sei già in lista per te: questa iscrizione è per un'altra persona.
            </p>
          ) : (
            <IonSegment value={forWho} onIonChange={(e) => this.setState({ forWho: e.detail.value })}>
              <IonSegmentButton value="me">
                <IonLabel>Per me</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="minor">
                <IonLabel>Per un'altra persona</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          )}

          {combinable.length > 0 && (
            <>
              <IonLabel position="stacked" className="ion-text-wrap">
                <b>Vuoi ricevere anche questi vaccini nella stessa seduta?</b>
              </IonLabel>

              <IonList>
                {combinable.map((other) => (
                  <IonItem key={other.id} lines="none">
                    <IonCheckbox
                      slot="start"
                      checked={extraTypeIds.includes(other.id)}
                      onIonChange={() => this.toggleExtraType(other.id)}
                    />
                    <IonLabel className="ion-text-wrap">{other.name}</IonLabel>
                  </IonItem>
                ))}
              </IonList>

              <p style={{ color: 'var(--ion-color-medium)', fontSize: '13px' }}>
                Scegliendone almeno uno entri in un'unica lista d'attesa combinata: riceverai una sola
                proposta di appuntamento per tutti i vaccini richiesti.
              </p>
            </>
          )}

          <IonList>
            {forWho === 'minor' && (
              <>
                <IonItem>
                  <IonLabel position="stacked">Nome e cognome dell'altra persona *</IonLabel>
                  <IonInput
                    value={minorName}
                    placeholder="Es. Luca Rossi"
                    onIonChange={(e) => this.setState({ minorName: e.detail.value })}
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Codice fiscale dell'altra persona *</IonLabel>
                  <IonInput
                    value={minorFiscalCode}
                    placeholder="RSSLCU15A01F205X"
                    style={{ textTransform: 'uppercase' }}
                    maxlength={16}
                    onIonChange={(e) => this.setState({ minorFiscalCode: e.detail.value })}
                  />
                </IonItem>
              </>
            )}

            <IonItem>
              <IonLabel position="stacked">Età del paziente *</IonLabel>
              <IonInput
                type="number"
                min="1"
                max="120"
                value={patientAge}
                placeholder="Es. 65"
                onIonChange={(e) => this.setState({ patientAge: e.detail.value })}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Altre patologie</IonLabel>
              <IonTextarea
                value={otherPathologies}
                rows={3}
                placeholder="Indica eventuali patologie rilevanti (facoltativo)"
                onIonChange={(e) => this.setState({ otherPathologies: e.detail.value })}
              />
            </IonItem>

            <IonItem lines="none" className="item-label-stacked">
              <IonLabel position="stacked">Ha già fatto altri vaccini? *</IonLabel>
              <IonSegment
                className="ion-margin-top"
                value={otherVaccinesDone === null ? undefined : otherVaccinesDone ? 'yes' : 'no'}
                onIonChange={(e) => this.setState({ otherVaccinesDone: e.detail.value === 'yes' })}
              >
                <IonSegmentButton value="yes">
                  <IonLabel>Sì</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="no">
                  <IonLabel>No</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonItem>

            {otherVaccinesDone === true && (
              <IonItem>
                <IonLabel position="stacked">Quali altri vaccini e quando</IonLabel>
                <IonTextarea
                  value={otherVaccinesNotes}
                  rows={3}
                  placeholder="Es. antinfluenzale a novembre 2025 (facoltativo)"
                  onIonChange={(e) => this.setState({ otherVaccinesNotes: e.detail.value })}
                />
              </IonItem>
            )}

            <IonItem lines="none" className="item-label-stacked">
              <IonLabel position="stacked" className="ion-text-wrap">
                {forWho === 'minor' ? "L'altra persona ha" : 'Hai'} già ricevuto lo stesso vaccino in passato? *
                {requiresPrior && (
                  <>
                    <br />
                    <small>In lista d'attesa entrano solo i pazienti che hanno già ricevuto lo stesso vaccino.</small>
                  </>
                )}
              </IonLabel>
              <IonSegment
                className="ion-margin-top"
                value={declared === null ? undefined : declared ? 'yes' : 'no'}
                onIonChange={(e) => this.setState({ declared: e.detail.value === 'yes' })}
              >
                <IonSegmentButton value="yes">
                  <IonLabel>Sì</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="no">
                  <IonLabel>No</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonItem>
          </IonList>

          <IonButton expand="block" disabled={!this.canSubmit() || submitting} onClick={this.submit}>
            {submitting ? <IonSpinner name="dots" /> : "Richiedi iscrizione in lista d'attesa"}
          </IonButton>

          {declared === false && requiresPrior && (
            <p className="ion-text-center" style={{ color: 'var(--ion-color-danger)', fontSize: '13px' }}>
              {forWho === 'minor'
                ? "L'altra persona non ha ancora ricevuto questo vaccino: l'iscrizione va fatta in farmacia."
                : "Non hai ancora ricevuto questo vaccino: l'iscrizione va fatta in farmacia."}
            </p>
          )}

          {(declared === null || otherVaccinesDone === null) && (
            <p className="ion-text-center" style={{ color: 'var(--ion-color-medium)', fontSize: '13px' }}>
              Rispondi a tutte le domande obbligatorie per iscriverti.
            </p>
          )}
        </IonContent>
      </IonModal>
    );
  }

  renderProposalModal() {
    const { proposalGroup, responding } = this.state;

    const proposals = proposalGroup?.proposals || [];
    const vaccines = proposals.map((proposal) => proposal.vaccine_name).filter(Boolean).join(' + ');
    const patients = proposals
      .map((proposal) =>
        proposal.fiscalcode_name ? `${proposal.fiscalcode_name} (${proposal.codice_fiscale})` : proposal.codice_fiscale
      )
      .filter(Boolean)
      .join(', ');

    return (
      <IonModal isOpen={!!proposalGroup} onDidDismiss={this.closeProposalModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Proposta di appuntamento</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={this.closeProposalModal}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <h2>{vaccines}</h2>

          <p>
            <strong>Appuntamento:</strong> {formatDateTime(proposalGroup?.scheduled_at)}
          </p>
          <p>
            <strong>Rispondi entro:</strong> {formatDateTime(proposalGroup?.response_deadline)}
          </p>
          {patients && (
            <p>
              <strong>Per:</strong> {patients}
            </p>
          )}

          <p style={{ color: 'var(--ion-color-medium)', fontSize: '13px' }}>
            Rifiutando la proposta torni in lista d'attesa con priorità ridotta.
          </p>

          <IonButton
            expand="block"
            color="success"
            disabled={responding}
            onClick={() => this.respondToProposal('confirmed')}
          >
            {responding ? <IonSpinner name="dots" /> : 'Accetta'}
          </IonButton>

          <IonButton
            expand="block"
            fill="outline"
            color="danger"
            disabled={responding}
            onClick={() => this.respondToProposal('refused')}
          >
            Rifiuta
          </IonButton>
        </IonContent>
      </IonModal>
    );
  }

  render() {
    const { types, loading, alert, toast } = this.state;

    return (
      <>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/app/home" text="" />
            </IonButtons>
            <IonTitle>Vaccini</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => this.props.history.push('/app/vaccini/miei')}>I miei vaccini</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {loading ? (
            <div className="ion-text-center ion-padding">
              <IonSpinner />
            </div>
          ) : (
            <IonList>
              {types.map((type) => {
                const entry = this.entryForMe(type);
                const excerpt = descriptionExcerpt(type);

                return (
                  <IonItem key={type.id} button detail={false} onClick={() => this.handleTypeClick(type)}>
                    <IonLabel className="ion-text-wrap">
                      <h2>{type.name}</h2>
                      {excerpt && <p>{excerpt}</p>}
                      {entry ? (
                        <IonBadge color="success">
                          {entry.status === 'waiting' && 'Sei in lista'}
                          {entry.status === 'proposed' && 'Proposta in corso'}
                          {entry.status === 'confirmed' && 'Appuntamento confermato'}
                        </IonBadge>
                      ) : (
                        <IonBadge color="primary">Lista d'attesa aperta</IonBadge>
                      )}
                    </IonLabel>
                    <IonIcon
                      slot="end"
                      color={entry && entry.status === 'waiting' ? 'danger' : undefined}
                      icon={entry && entry.status === 'waiting' ? trash : forward}
                    />
                  </IonItem>
                );
              })}

              {!types.length && (
                <IonItem lines="none">
                  <IonLabel className="ion-text-wrap">Nessuna lista d'attesa aperta al momento.</IonLabel>
                </IonItem>
              )}
            </IonList>
          )}

          {this.renderModal()}
          {this.renderProposalModal()}

          <IonAlert
            isOpen={!!alert}
            header={alert?.header}
            message={alert?.message}
            buttons={[
              { text: 'Annulla', role: 'cancel' },
              {
                text: "Iscrivi un'altra persona",
                handler: () => alert?.type && this.openModal(alert.type, 'minor'),
              },
              {
                text: 'Cancella iscrizione',
                role: 'destructive',
                handler: () => alert?.entryId && this.removeEntry(alert.entryId),
              },
            ]}
            onDidDismiss={() => this.setState({ alert: null })}
          />

          <IonToast
            isOpen={!!toast}
            message={toast || ''}
            duration={4000}
            onDidDismiss={() => this.setState({ toast: null })}
          />
        </IonContent>
      </>
    );
  }
}

export default withIonLifeCycle(VaccinesPage);
